// ═══════════════════════════════════════════════════════════════════════════
// Dynamic Blocks — parameter schemas (Tier 1 + Tier 2 + Tier 3)
// ═══════════════════════════════════════════════════════════════════════════
//
// Contains every supported parameter kind plus the discriminated union
// (`dynamicParameterSchema`) and lookup helpers. The union is the single
// source of truth for the parameter shape — DXF parser / writer / evaluator
// / UI all hang off the `kind` discriminator.
//
// Tier 1 kinds: linear, flip, visibility, basepoint
// Tier 2 kinds: point, polar, xy, rotation, lookup  (added in DYN-2)
// Tier 2 constraint kinds: constraint-horizontal, constraint-vertical,
//                          constraint-angular  (added in DYN-2 Batch C)
// Tier 3 kinds: alignment (auto-rotation to geometry, no action required)
// Tier 3 constraint kinds: constraint-aligned (distance along arbitrary
//                          axis), constraint-radial (circle radius),
//                          constraint-diameter (2× radius)
//
// Every schema follows AutoCAD's naming conventions (basePoint, secondPoint,
// currentDistance, defaultAngle, valueSet, invisibleEntityIds, etc.) so the
// DXF round-trip lands the data in the expected slots without renaming.

import { z } from "zod";

import {
  baseParameterSchema,
  drawingPointSchema,
  valueSetSchema,
} from "./dynamicBlockCommon";

// ═══════════════════════════════════════════════════════════════════════════
// ASSOCIATED ENTITY REF — DXF ACDBASSOCGEOMDEPENDENCY back-reference
// ═══════════════════════════════════════════════════════════════════════════
//
// AutoCAD dimensional constraint parameters (horizontal / vertical /
// aligned / angular / radial / diameter) carry persistent back-references
// to the geometry they dimension. The references live in
// `ACDBASSOCGEOMDEPENDENCY` records inside an
// `ACDBASSOC2DCONSTRAINTGROUP`; each geom-dep carries the constrained
// entity handle plus a pair of `AcDbAssocEdgePersSubentId` integers that
// identify the feature sub-element (edge, vertex, face) on the entity.
//
// The parser links a constraint parameter to its geom-deps by matching
// the parameter's value-dep handle (captured from
// `AcDbBlockConstraintParameter 330 <valueDep>`) against the geom-dep's
// chain handles (330 codes inside the `AcDbAssocDependency` subclass
// block). When the chain doesn't match, the parser falls back to
// group-level association (every geom-dep in the parameter's
// constraint group).
//
// `subentType` is the first `90` after the `AcDbAssocEdgePersSubentId`
// marker — observed values are `0` (edge) in АБИ1.dxf. `subentIdA` /
// `subentIdB` are the following two `90` integers; they identify a pair
// of persistent vertex IDs on an LWPOLYLINE edge (see DXF group 91 on
// the polyline body). The block-evaluator `resolveSubselector` helper
// maps the pair to a canonical subselector
// ("start"/"end"/"v<i>"/"edge<i>"/"center").
//
// `isSingleEdge` distinguishes the two PersSubentId classes AutoCAD
// emits:
//   - `AcDbAssocEdgePersSubentId` (isSingleEdge = false) — two 90 code
//     pairs name a vertex pair on an LWPOLYLINE.
//   - `AcDbAssocSingleEdgePersSubentId` (isSingleEdge = true) — no 90
//     code pairs; the whole LINE / CIRCLE / ARC is the feature.
// The discriminator is the DXF `1` code after the
// `AcDbAssocGeomDependency` subclass marker — captured in the assocdeps
// parser and forwarded through `ResolvedAssocEntityRef`.
export const associatedEntityRefSchema = z.object({
  entityId: z.string().default(""),
  subentType: z.number().int().default(0),
  subentIdA: z.number().int().default(0),
  subentIdB: z.number().int().default(0),
  // Optional for backwards-compatibility with legacy DXF fixtures that
  // predate the SingleEdge/Edge discriminator wiring. Missing / undefined
  // is treated as "unknown, fall back to coordinate match" in the
  // subselector resolver. The assocdeps parser always emits this field
  // on freshly-imported documents.
  isSingleEdge: z.boolean().optional(),
});
export type AssociatedEntityRef = z.infer<typeof associatedEntityRefSchema>;

// ═══════════════════════════════════════════════════════════════════════════
// LINEAR — distance along fixed direction (basePoint → secondPoint)
// ═══════════════════════════════════════════════════════════════════════════
export const linearParameterSchema = baseParameterSchema.extend({
  kind: z.literal("linear"),
  basePoint: drawingPointSchema,
  secondPoint: drawingPointSchema,
  defaultDistance: z.number().default(0),
  currentDistance: z.number().default(0),
  valueSet: valueSetSchema.default(() => valueSetSchema.parse({})),
});
export type LinearParameter = z.infer<typeof linearParameterSchema>;

// ═══════════════════════════════════════════════════════════════════════════
// FLIP — boolean mirror across axis
// ═══════════════════════════════════════════════════════════════════════════
export const flipParameterSchema = baseParameterSchema.extend({
  kind: z.literal("flip"),
  axisStart: drawingPointSchema,
  axisEnd: drawingPointSchema,
  defaultValue: z.boolean().default(false),
  currentValue: z.boolean().default(false),
  flipLabel: z.string().default("Flip"),
  baseLabel: z.string().default("Base"),
});
export type FlipParameter = z.infer<typeof flipParameterSchema>;

// ═══════════════════════════════════════════════════════════════════════════
// VISIBILITY — named states + invisible-entity lists
// ═══════════════════════════════════════════════════════════════════════════
export const visibilityStateSchema = z.object({
  id: z.string().min(1),
  name: z.string().min(1),
  invisibleEntityIds: z.array(z.string()).default([]),
});
export type VisibilityState = z.infer<typeof visibilityStateSchema>;

export const visibilityParameterSchema = baseParameterSchema.extend({
  kind: z.literal("visibility"),
  position: drawingPointSchema,
  states: z.array(visibilityStateSchema).min(1),
  defaultStateId: z.string().min(1),
  currentStateId: z.string().min(1),
});
export type VisibilityParameter = z.infer<typeof visibilityParameterSchema>;

// ═══════════════════════════════════════════════════════════════════════════
// BASE POINT — override block insertion origin
// ═══════════════════════════════════════════════════════════════════════════
export const basePointParameterSchema = baseParameterSchema.extend({
  kind: z.literal("basepoint"),
  position: drawingPointSchema,
});
export type BasePointParameter = z.infer<typeof basePointParameterSchema>;

// ═══════════════════════════════════════════════════════════════════════════
// POINT — 2D offset from basePoint (DYN-2 Tier 2)
// ═══════════════════════════════════════════════════════════════════════════
//
// A Point Parameter lets the user drag a single grip in 2D, independently
// along X and Y. AutoCAD stores the base point and the current point; the
// offset is computed as `currentPoint - basePoint`. Each axis has its own
// Value Set (the user can snap only X or only Y to an increment).
export const pointParameterSchema = baseParameterSchema.extend({
  kind: z.literal("point"),
  basePoint: drawingPointSchema,
  currentPoint: drawingPointSchema,
  xValueSet: valueSetSchema.default(() => valueSetSchema.parse({})),
  yValueSet: valueSetSchema.default(() => valueSetSchema.parse({})),
});
export type PointParameter = z.infer<typeof pointParameterSchema>;

// ═══════════════════════════════════════════════════════════════════════════
// POLAR — distance + angle (DYN-2 Tier 2)
// ═══════════════════════════════════════════════════════════════════════════
//
// A Polar Parameter has a fixed base point, and the user can drag the
// second point in polar space — both distance AND angle change. Two Value
// Sets (distance + angle) snap independently. Stored angle is in DEGREES
// (matches AutoCAD property-palette display) but evaluators convert to
// radians internally.
export const polarParameterSchema = baseParameterSchema.extend({
  kind: z.literal("polar"),
  basePoint: drawingPointSchema,
  secondPoint: drawingPointSchema,
  defaultDistance: z.number().default(0),
  defaultAngle: z.number().default(0),
  currentDistance: z.number().default(0),
  currentAngle: z.number().default(0),
  distanceValueSet: valueSetSchema.default(() => valueSetSchema.parse({})),
  angleValueSet: valueSetSchema.default(() => valueSetSchema.parse({})),
});
export type PolarParameter = z.infer<typeof polarParameterSchema>;

// ═══════════════════════════════════════════════════════════════════════════
// XY — orthogonal X / Y distances (DYN-2 Tier 2)
// ═══════════════════════════════════════════════════════════════════════════
//
// An XY Parameter has a base point and a second point; the user drags the
// second point and X / Y distances update independently (not polar). Used
// typically by XY Array actions (columns × rows).
export const xyParameterSchema = baseParameterSchema.extend({
  kind: z.literal("xy"),
  basePoint: drawingPointSchema,
  secondPoint: drawingPointSchema,
  defaultX: z.number().default(0),
  defaultY: z.number().default(0),
  currentX: z.number().default(0),
  currentY: z.number().default(0),
  xValueSet: valueSetSchema.default(() => valueSetSchema.parse({})),
  yValueSet: valueSetSchema.default(() => valueSetSchema.parse({})),
});
export type XYParameter = z.infer<typeof xyParameterSchema>;

// ═══════════════════════════════════════════════════════════════════════════
// ROTATION — angular parameter (DYN-2 Tier 2)
// ═══════════════════════════════════════════════════════════════════════════
//
// A Rotation Parameter has a center (basePoint), a radius (grip distance),
// and a current angle in DEGREES. The attached Rotate action rotates its
// selection around basePoint by `(currentAngle - defaultAngle) *
// angleMultiplier`.
export const rotationParameterSchema = baseParameterSchema.extend({
  kind: z.literal("rotation"),
  basePoint: drawingPointSchema,
  radius: z.number().default(1),
  defaultAngle: z.number().default(0),
  currentAngle: z.number().default(0),
  angleValueSet: valueSetSchema.default(() => valueSetSchema.parse({})),
});
export type RotationParameter = z.infer<typeof rotationParameterSchema>;

// ═══════════════════════════════════════════════════════════════════════════
// LOOKUP — indexes into a lookup table (DYN-2 Tier 2)
// ═══════════════════════════════════════════════════════════════════════════
//
// A Lookup Parameter has no numeric value of its own; it holds a reference
// to a LookupTable (by id) and a current-row id. Selecting a new row cascades
// updates to the other parameters listed in the table's input columns
// (that's the semantics of the Lookup action — see dynamicBlockActions).
//
// `reverseLookup` controls whether the Properties-palette dropdown exposes
// the output column for selection (reverse) or only the input columns
// (forward). AutoCAD's "Allow reverse lookup" checkbox maps here.
export const lookupParameterSchema = baseParameterSchema.extend({
  kind: z.literal("lookup"),
  position: drawingPointSchema,
  reverseLookup: z.boolean().default(false),
  lookupTableId: z.string().min(1),
  currentLookupRowId: z.string().optional(),
});
export type LookupParameter = z.infer<typeof lookupParameterSchema>;

// ═══════════════════════════════════════════════════════════════════════════
// HORIZONTAL CONSTRAINT — fixes |pointB.x − pointA.x| (DYN-2 Batch C)
// ═══════════════════════════════════════════════════════════════════════════
//
// AutoCAD's "Distance Constraint, Horizontal" — a dimensional constraint
// that locks the X-distance between two named points to a numeric value.
// Exposed in the Properties Palette as an editable numeric (just like a
// Linear parameter), so Move / Stretch / Scale / Array actions can be
// linked to it. The full constraint solver (Gauss-Newton) lands in DYN-3;
// in Tier 2 the parameter behaves as a numeric-driven driver:
//   * authored value → currentDistance
//   * connected actions translate / scale linked entities along X
//   * direct propagation (single-axis, no solver dependency)
//
// Stored fields mirror Linear parameter (basePoint / secondPoint pair) so
// the UI / grip / DXF round-trip pipelines reuse Linear plumbing where
// possible — see `isConstraintLinearLikeParameter` below.
export const horizontalConstraintParameterSchema = baseParameterSchema.extend({
  kind: z.literal("constraint-horizontal"),
  pointA: drawingPointSchema,
  pointB: drawingPointSchema,
  defaultDistance: z.number().default(0),
  currentDistance: z.number().default(0),
  distanceValueSet: valueSetSchema.default(() => valueSetSchema.parse({})),
  // DXF-sourced explicit entity references (see associatedEntityRefSchema).
  // Empty when the parameter was synthesised by a fixture / older parser.
  associatedEntities: z.array(associatedEntityRefSchema).optional(),
});
export type HorizontalConstraintParameter = z.infer<
  typeof horizontalConstraintParameterSchema
>;

// ═══════════════════════════════════════════════════════════════════════════
// VERTICAL CONSTRAINT — fixes |pointB.y − pointA.y| (DYN-2 Batch C)
// ═══════════════════════════════════════════════════════════════════════════
//
// Same shape as Horizontal constraint, applied along Y instead of X.
export const verticalConstraintParameterSchema = baseParameterSchema.extend({
  kind: z.literal("constraint-vertical"),
  pointA: drawingPointSchema,
  pointB: drawingPointSchema,
  defaultDistance: z.number().default(0),
  currentDistance: z.number().default(0),
  distanceValueSet: valueSetSchema.default(() => valueSetSchema.parse({})),
  associatedEntities: z.array(associatedEntityRefSchema).optional(),
});
export type VerticalConstraintParameter = z.infer<
  typeof verticalConstraintParameterSchema
>;

// ═══════════════════════════════════════════════════════════════════════════
// ANGULAR CONSTRAINT — fixes the angle ∠(vertex→pointA, vertex→pointB)
// (DYN-2 Batch C)
// ═══════════════════════════════════════════════════════════════════════════
//
// AutoCAD's "Angular Constraint" — locks the angle between two rays
// emanating from a shared vertex. Stored in DEGREES (matches AutoCAD
// property-palette display); evaluators convert to radians internally.
// Move / Rotate actions can be linked to drive selection by the angle delta.
export const angularConstraintParameterSchema = baseParameterSchema.extend({
  kind: z.literal("constraint-angular"),
  vertex: drawingPointSchema,
  pointA: drawingPointSchema,
  pointB: drawingPointSchema,
  defaultAngle: z.number().default(0),
  currentAngle: z.number().default(0),
  angleValueSet: valueSetSchema.default(() => valueSetSchema.parse({})),
  associatedEntities: z.array(associatedEntityRefSchema).optional(),
});
export type AngularConstraintParameter = z.infer<
  typeof angularConstraintParameterSchema
>;

// ═══════════════════════════════════════════════════════════════════════════
// ALIGNMENT — auto-rotation anchor (DYN-3 Tier 3)
// ═══════════════════════════════════════════════════════════════════════════
//
// AutoCAD's Alignment Parameter is unique: it is NOT driven by actions.
// Instead, the evaluator / INSERT command auto-rotates the whole block so
// that the Alignment grip snaps to the geometry under the cursor (edge
// direction).
//
// `alignmentType`:
//   - "perpendicular" (AutoCAD default): the alignment axis aligns to the
//     NORMAL of the target edge (i.e. the block rotates so the alignment
//     direction is perpendicular to the detected geometry).
//   - "parallel": the alignment axis aligns WITH the target edge direction.
//
// Runtime state: `currentAngle` is overridden per INSERT via the
// `<name>.angle` customProperties key. Evaluator applies the resulting
// rotation around `position` to every child entity. When no override is
// provided the block renders in its authored orientation.
export const alignmentParameterSchema = baseParameterSchema.extend({
  kind: z.literal("alignment"),
  position: drawingPointSchema,
  defaultAngle: z.number().default(0),
  currentAngle: z.number().default(0),
  alignmentType: z.enum(["perpendicular", "parallel"]).default("perpendicular"),
});
export type AlignmentParameter = z.infer<typeof alignmentParameterSchema>;

// ═══════════════════════════════════════════════════════════════════════════
// ALIGNED CONSTRAINT — Euclidean distance between two points (DYN-3 Tier 3)
// ═══════════════════════════════════════════════════════════════════════════
//
// AutoCAD "Distance Constraint, Aligned" — locks |pointB − pointA| along
// the line AB (not axis-aligned). The driving axis angle derives from the
// authored direction (atan2(pointB.y - pointA.y, pointB.x - pointA.x)).
//
// NOTE: AutoCAD treats "Linear" and "Aligned" dimensional constraints as
// effectively identical in semantics — Linear is just the Aligned variant
// with its authored axis locked at the time of placement. We therefore
// keep a single schema kind here (`constraint-aligned`) that covers both
// AutoCAD dimension classes. The driver is fully compatible with Move /
// Stretch / Scale / Array actions (linear-like 1D numeric).
export const alignedConstraintParameterSchema = baseParameterSchema.extend({
  kind: z.literal("constraint-aligned"),
  pointA: drawingPointSchema,
  pointB: drawingPointSchema,
  defaultDistance: z.number().default(0),
  currentDistance: z.number().default(0),
  distanceValueSet: valueSetSchema.default(() => valueSetSchema.parse({})),
  associatedEntities: z.array(associatedEntityRefSchema).optional(),
});
export type AlignedConstraintParameter = z.infer<
  typeof alignedConstraintParameterSchema
>;

// ═══════════════════════════════════════════════════════════════════════════
// RADIAL CONSTRAINT — fixes circle / arc radius (DYN-3 Tier 3)
// ═══════════════════════════════════════════════════════════════════════════
//
// Locks the distance between `center` and `radiusPoint` (a reference point
// on the circle). Drives Scale actions uniformly (scale factor = current /
// default radius). Drives Move / Stretch conceptually via direct propagation
// along the radial direction `center → radiusPoint`.
export const radialConstraintParameterSchema = baseParameterSchema.extend({
  kind: z.literal("constraint-radial"),
  center: drawingPointSchema,
  radiusPoint: drawingPointSchema,
  defaultRadius: z.number().default(0),
  currentRadius: z.number().default(0),
  radiusValueSet: valueSetSchema.default(() => valueSetSchema.parse({})),
  associatedEntities: z.array(associatedEntityRefSchema).optional(),
});
export type RadialConstraintParameter = z.infer<
  typeof radialConstraintParameterSchema
>;

// ═══════════════════════════════════════════════════════════════════════════
// DIAMETER CONSTRAINT — fixes circle diameter (2× radius) (DYN-3 Tier 3)
// ═══════════════════════════════════════════════════════════════════════════
//
// Same geometry as Radial but the displayed / edited value is diameter
// (point1 → point2 distance through the center). Drives Scale uniformly
// (factor = current / default diameter); Move / Stretch via the point1 →
// point2 direction.
export const diameterConstraintParameterSchema = baseParameterSchema.extend({
  kind: z.literal("constraint-diameter"),
  center: drawingPointSchema,
  point1: drawingPointSchema,
  point2: drawingPointSchema,
  defaultDiameter: z.number().default(0),
  currentDiameter: z.number().default(0),
  diameterValueSet: valueSetSchema.default(() => valueSetSchema.parse({})),
  associatedEntities: z.array(associatedEntityRefSchema).optional(),
});
export type DiameterConstraintParameter = z.infer<
  typeof diameterConstraintParameterSchema
>;

// ═══════════════════════════════════════════════════════════════════════════
// USER — named scalar backed by an ACDBASSOCVARIABLE (DYN-4 Fix 3)
// ═══════════════════════════════════════════════════════════════════════════
//
// AutoCAD's "user parameter" is a named scalar whose value lives in an
// associative network (`ACDBASSOCVARIABLE`) rather than being encoded
// directly in the authoring graph. It is the driver that a
// constraint-based parametric block uses as the key column for a
// `BLOCKPROPERTIESTABLE` lookup — e.g. the "Двутавр" user parameter on
// the ibeam block selects a table row, which in turn drives the output
// columns ("Площадь", "Масса", …).
//
// Shape:
//   - `associatedVariableHandle` — DXF handle of the backing
//     ACDBASSOCVARIABLE. Used by the evaluator / writer to round-trip the
//     association.
//   - `variableName` — resolved name from the associated variable. When
//     the variable is missing we fall back to `name` (the parameter's own
//     id / label) so the Properties Palette still has something to show.
//   - `valueType` — decoded from the ACDBASSOCVARIABLE's group 90
//     (`real` / `distance` / `area` / `angle` / `string`).
//   - `defaultValue` — the current value of the variable at author time;
//     numeric for scalar types, string for `string` type.
//   - `expression` — opaque `AcDbCalc:1.0` payload preserved for
//     round-trip. Full expression evaluation is out of scope for this
//     phase; the payload is retained so a later phase can wire it in
//     without re-parsing the DXF.
export const userParameterSchema = baseParameterSchema.extend({
  kind: z.literal("user"),
  associatedVariableHandle: z.string().default(""),
  variableName: z.string().default(""),
  valueType: z
    .enum(["real", "distance", "area", "angle", "string", "unknown"])
    .default("unknown"),
  defaultValue: z.union([z.number(), z.string()]).default(0),
  expression: z.string().optional(),
});
export type UserParameter = z.infer<typeof userParameterSchema>;

// ═══════════════════════════════════════════════════════════════════════════
// Parameter discriminated union
// ═══════════════════════════════════════════════════════════════════════════
//
// Order of members is not semantic — the discriminator is `kind`. Keeping
// Tier 1 kinds first preserves git-blame clarity. Tier 2 constraint kinds
// (constraint-*) are appended after the Tier 2 numeric kinds; Tier 3 kinds
// follow at the end.
export const dynamicParameterSchema = z.discriminatedUnion("kind", [
  linearParameterSchema,
  flipParameterSchema,
  visibilityParameterSchema,
  basePointParameterSchema,
  pointParameterSchema,
  polarParameterSchema,
  xyParameterSchema,
  rotationParameterSchema,
  lookupParameterSchema,
  horizontalConstraintParameterSchema,
  verticalConstraintParameterSchema,
  angularConstraintParameterSchema,
  alignmentParameterSchema,
  alignedConstraintParameterSchema,
  radialConstraintParameterSchema,
  diameterConstraintParameterSchema,
  // DYN-4 Fix 3 — named scalar sourced from ACDBASSOCVARIABLE.
  userParameterSchema,
]);
export type DynamicParameter = z.infer<typeof dynamicParameterSchema>;

// ═══════════════════════════════════════════════════════════════════════════
// Lookup helpers (parameter-scoped — keep near their schemas)
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Returns true when the parameter carries a numeric value compatible with
 * Move / Stretch / Scale / Array actions driven by a linear-axis delta.
 *
 * Tier 1: linear.
 * Tier 2: polar, xy (scale uses these too).
 * Tier 2 constraints: horizontal, vertical (single-axis numeric drivers).
 * Tier 3 constraints: aligned, radial, diameter (arbitrary axis / radius).
 */
export function isLinearLikeParameter(
  parameter: DynamicParameter,
): parameter is
  | LinearParameter
  | PolarParameter
  | XYParameter
  | HorizontalConstraintParameter
  | VerticalConstraintParameter
  | AlignedConstraintParameter
  | RadialConstraintParameter
  | DiameterConstraintParameter {
  return (
    parameter.kind === "linear" ||
    parameter.kind === "polar" ||
    parameter.kind === "xy" ||
    parameter.kind === "constraint-horizontal" ||
    parameter.kind === "constraint-vertical" ||
    parameter.kind === "constraint-aligned" ||
    parameter.kind === "constraint-radial" ||
    parameter.kind === "constraint-diameter"
  );
}

/**
 * Returns true when the parameter carries an angular value compatible with
 * Rotate actions. Tier 2: rotation. Tier 2 constraints: angular.
 */
export function isAngularLikeParameter(
  parameter: DynamicParameter,
): parameter is RotationParameter | AngularConstraintParameter {
  return (
    parameter.kind === "rotation" || parameter.kind === "constraint-angular"
  );
}

export function findVisibilityStateById(
  parameter: VisibilityParameter,
  id: string,
): VisibilityState | undefined {
  return parameter.states.find((s) => s.id === id);
}
